import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as dashboardSummaryAPI from '../../api/FinanceReportAPI/dashboardSummaryAPI';

// 1. Receivables (Client Outstanding Summary)
export const fetchClientOutStandingSummary = createAsyncThunk(
    'financialOverview/fetchClientOutStandingSummary',
    async ({ roleId, userId } = {}, { rejectWithValue }) => {
        try {
            const response = await dashboardSummaryAPI.getClientOutStandingSummary({ roleId, userId });
            return response?.Data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch Client Outstanding Summary');
        }
    }
);

// 2. Payables (Vendor Outstanding Summary)
export const fetchVendorOutStandingSummary = createAsyncThunk(
    'financialOverview/fetchVendorOutStandingSummary',
    async ({ roleId, userId } = {}, { rejectWithValue }) => {
        try {
            const response = await dashboardSummaryAPI.getVendorOutStandingSummary({ roleId, userId });
            return response?.Data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch Vendor Outstanding Summary');
        }
    }
);

// 3. Tax Liabilities (GST position card - previous month GST). Thunk name kept so the dashboards
//    that dispatch it are unchanged; it now reads Reports/GetGSTPositionSummary.
export const fetchStockPurchaseConsolidateSummary = createAsyncThunk(
    'financialOverview/fetchStockPurchaseConsolidateSummary',
    async ({ roleId, userId } = {}, { rejectWithValue }) => {
        try {
            const response = await dashboardSummaryAPI.getGSTPositionSummary({ roleId, userId });
            return response?.Data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch GST position summary');
        }
    }
);

// 4. FY Income / Expenses / Provisional Margin (current FY vs previous FY)
export const fetchFYIncomeExpenseSummary = createAsyncThunk(
    'financialOverview/fetchFYIncomeExpenseSummary',
    async ({ fyStartYear } = {}, { rejectWithValue }) => {
        try {
            const response = await dashboardSummaryAPI.getFYIncomeExpenseSummary({ fyStartYear });
            return response?.Data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch FY Income / Expense Summary');
        }
    }
);

// 6. Company Overhead % (Consolidate Cash Flow overhead), current FY vs previous FY
export const fetchOverheadPercentSummary = createAsyncThunk(
    'financialOverview/fetchOverheadPercentSummary',
    async ({ fyStartYear } = {}, { rejectWithValue }) => {
        try {
            const response = await dashboardSummaryAPI.getOverheadPercentSummary({ fyStartYear });
            return response?.Data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch Overhead % Summary');
        }
    }
);

// 5. This Month / Last Month Sales + Purchase (pre-tax BasicValue)
export const fetchMonthSalesPurchaseSummary = createAsyncThunk(
    'financialOverview/fetchMonthSalesPurchaseSummary',
    async ({ roleId, userId } = {}, { rejectWithValue }) => {
        try {
            const response = await dashboardSummaryAPI.getMonthSalesPurchaseSummary({ roleId, userId });
            return response?.Data || response;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to fetch Month Sales / Purchase Summary');
        }
    }
);

const initialState = {
    receivables: null,
    payables: null,
    taxLiabilities: null,
    incomeExpense: null,
    salesPurchase: null,
    overhead: null,
    loading: {
        receivables: false,
        payables: false,
        taxLiabilities: false,
        incomeExpense: false,
        salesPurchase: false,
        overhead: false,
    },
    errors: {
        receivables: null,
        payables: null,
        taxLiabilities: null,
        incomeExpense: null,
        salesPurchase: null,
        overhead: null,
    },
};

const financialOverviewSlice = createSlice({
    name: 'financialOverview',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchClientOutStandingSummary.pending, (state) => {
                state.loading.receivables = true;
                state.errors.receivables = null;
            })
            .addCase(fetchClientOutStandingSummary.fulfilled, (state, action) => {
                state.loading.receivables = false;
                state.receivables = action.payload;
            })
            .addCase(fetchClientOutStandingSummary.rejected, (state, action) => {
                state.loading.receivables = false;
                state.errors.receivables = action.payload;
            })

            .addCase(fetchVendorOutStandingSummary.pending, (state) => {
                state.loading.payables = true;
                state.errors.payables = null;
            })
            .addCase(fetchVendorOutStandingSummary.fulfilled, (state, action) => {
                state.loading.payables = false;
                state.payables = action.payload;
            })
            .addCase(fetchVendorOutStandingSummary.rejected, (state, action) => {
                state.loading.payables = false;
                state.errors.payables = action.payload;
            })

            .addCase(fetchStockPurchaseConsolidateSummary.pending, (state) => {
                state.loading.taxLiabilities = true;
                state.errors.taxLiabilities = null;
            })
            .addCase(fetchStockPurchaseConsolidateSummary.fulfilled, (state, action) => {
                state.loading.taxLiabilities = false;
                state.taxLiabilities = action.payload;
            })
            .addCase(fetchStockPurchaseConsolidateSummary.rejected, (state, action) => {
                state.loading.taxLiabilities = false;
                state.errors.taxLiabilities = action.payload;
            })

            .addCase(fetchFYIncomeExpenseSummary.pending, (state) => {
                state.loading.incomeExpense = true;
                state.errors.incomeExpense = null;
            })
            .addCase(fetchFYIncomeExpenseSummary.fulfilled, (state, action) => {
                state.loading.incomeExpense = false;
                state.incomeExpense = action.payload;
            })
            .addCase(fetchFYIncomeExpenseSummary.rejected, (state, action) => {
                state.loading.incomeExpense = false;
                state.errors.incomeExpense = action.payload;
            })

            .addCase(fetchMonthSalesPurchaseSummary.pending, (state) => {
                state.loading.salesPurchase = true;
                state.errors.salesPurchase = null;
            })
            .addCase(fetchMonthSalesPurchaseSummary.fulfilled, (state, action) => {
                state.loading.salesPurchase = false;
                state.salesPurchase = action.payload;
            })
            .addCase(fetchMonthSalesPurchaseSummary.rejected, (state, action) => {
                state.loading.salesPurchase = false;
                state.errors.salesPurchase = action.payload;
            })

            .addCase(fetchOverheadPercentSummary.pending, (state) => {
                state.loading.overhead = true;
                state.errors.overhead = null;
            })
            .addCase(fetchOverheadPercentSummary.fulfilled, (state, action) => {
                state.loading.overhead = false;
                state.overhead = action.payload;
            })
            .addCase(fetchOverheadPercentSummary.rejected, (state, action) => {
                state.loading.overhead = false;
                state.errors.overhead = action.payload;
            });
    },
});

export const selectReceivablesSummary = (state) => state.financialOverview.receivables;
export const selectPayablesSummary = (state) => state.financialOverview.payables;
export const selectTaxLiabilitiesSummary = (state) => state.financialOverview.taxLiabilities;
export const selectIncomeExpenseSummary = (state) => state.financialOverview.incomeExpense;
export const selectSalesPurchaseSummary = (state) => state.financialOverview.salesPurchase;

export const selectReceivablesLoading = (state) => state.financialOverview.loading.receivables;
export const selectPayablesLoading = (state) => state.financialOverview.loading.payables;
export const selectTaxLiabilitiesLoading = (state) => state.financialOverview.loading.taxLiabilities;
export const selectIncomeExpenseLoading = (state) => state.financialOverview.loading.incomeExpense;
export const selectOverheadSummary = (state) => state.financialOverview.overhead;
export const selectOverheadLoading = (state) => state.financialOverview.loading.overhead;

export default financialOverviewSlice.reducer;
